import { forwardRef } from 'react'
import { useControllableValue } from '@/ui/kit'
import { cn } from './internal/utils'
import { FOCUS_RING } from './internal/presets'
import { Indicator } from './Spin'

const SIZES = {
  default: { track: 'h-7 w-14', knob: 'h-5 w-5', travel: 'translate-x-8' },
  small: { track: 'h-5 w-10', knob: 'h-3.5 w-3.5', travel: 'translate-x-5' },
}

/**
 * antd Switch. `onChange` receives the next boolean plus the event, and the
 * control is a real `role="switch"` button so it is keyboard reachable.
 */
const Switch = forwardRef(
  (
    {
      checked,
      defaultChecked,
      onChange,
      onClick,
      disabled = false,
      loading = false,
      size = 'default',
      checkedChildren,
      unCheckedChildren,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    const [isChecked, setChecked] = useControllableValue(checked, defaultChecked ?? false)
    const isDisabled = disabled || loading
    const metrics = SIZES[size] || SIZES.default

    const toggle = (event) => {
      if (isDisabled) return
      const next = !isChecked
      setChecked(next)
      onChange?.(next, event)
      onClick?.(next, event)
    }

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={Boolean(isChecked)}
        disabled={isDisabled}
        onClick={toggle}
        className={cn(
          'ui-switch relative inline-flex shrink-0 items-center rounded-full transition-colors duration-[var(--duration-normal)]',
          FOCUS_RING,
          metrics.track,
          isChecked ? 'ui-switch-checked bg-[var(--primary-color)]' : 'bg-[var(--gray-500)]',
          isDisabled && 'ui-switch-disabled cursor-not-allowed opacity-60',
          className
        )}
        style={style}
        {...rest}
      >
        {checkedChildren || unCheckedChildren ? (
          <span
            className={cn(
              'ui-switch-inner absolute text-[10px] font-medium text-white',
              isChecked ? 'left-1.5' : 'right-1.5'
            )}
          >
            {isChecked ? checkedChildren : unCheckedChildren}
          </span>
        ) : null}
        <span
          className={cn(
            'ui-switch-handle pointer-events-none ml-0.5 flex items-center justify-center rounded-full bg-white shadow transition-transform duration-[var(--duration-normal)]',
            metrics.knob,
            isChecked && metrics.travel
          )}
        >
          {loading ? <Indicator size="small" className="h-2.5 w-2.5 border" /> : null}
        </span>
      </button>
    )
  }
)

Switch.displayName = 'Switch'

export default Switch
